<template>
  <div class="card" :class="{ 'h-100': isSummaryVariant, 'cash-card': !isSummaryVariant }">
    <div class="card-body" :class="{ 'cash-card-body': !isSummaryVariant }">
      <h5 v-if="isSummaryVariant" class="card-title">{{ t("freeFundsLabel") }} <strong>{{ money.c }}</strong></h5>
      <h5 v-else class="card-title cash-card-title">{{ t("cashLabel") }} ({{ money.c }})</h5>
      <div class="table-responsive">
        <table class="table" :class="{ 'cash-money-table': !isSummaryVariant }">
          <tbody v-if="isSummaryVariant">
            <tr>
              <td v-if="1200 > windowsWidth" :class="smallTextClass">{{ t("baseCurrencyLabel") }}</td>
              <td v-else>{{ t("balanceLabel") }} ({{ t("baseCurrencyLabel") }})</td>
              <td class="equity_align_right_heavy" v-if="'BGN' === currency" :class="[smallTextClass, { redColor: isNegativeAmount(money.a * moneyRatio) }]">
                {{ formatPrice(money.a * moneyRatio, currency, 2).replace(" ", "&nbsp;") }}
              </td>
              <td class="equity_align_right_heavy" v-if="'EUR' === currency" :class="[smallTextClass, { redColor: isNegativeAmount(money.a * moneyRatio / eurToBgnRatio) }]">
                {{ formatPrice(money.a * moneyRatio / eurToBgnRatio, currency, 2).replace(" ", "&nbsp;") }}
              </td>
            </tr>
            <tr>
              <td :class="smallTextClass">{{ t("balanceLabel") }}</td>
              <td class="equity_align_right_heavy" :class="[smallTextClass, { redColor: isNegativeAmount(money.a) }]">
                {{ formatPrice(money.a, money.c, 2).replace(" ", "&nbsp;") }}
              </td>
            </tr>
            <tr>
              <td :class="smallTextClass">%{{ t("cashLabel") }}</td>
              <td v-if="0 !== moneyProgressPercent" class="equity_align_right_heavy" :class="smallTextClass">
                <span class="progress-meter-value">
                  <meter :value="moneyProgressPercent" min="0" max="100"></meter>
                  <span class="progress-percent">{{
                    parseNumber(moneyProgressPercent)
                  }}%</span>
                </span>
              </td>
              <td v-else class="equity_align_right_heavy" :class="smallTextClass">
                <span class="progress-meter-value">
                  <meter value="0" min="0" max="100"></meter>
                  <span class="progress-percent">0%</span>
                </span>
              </td>
            </tr>
            <tr>
              <td :class="smallTextClass">%{{ t("totalBalanceLabel") }}</td>
              <td v-if="0 !== balanceProgressPercent" class="equity_align_right_heavy" :class="smallTextClass">
                <span class="progress-meter-value">
                  <meter :value="balanceProgressPercent" min="0" max="100"></meter>
                  <span class="progress-percent">{{
                    parseNumber(balanceProgressPercent)
                  }}%</span>
                </span>
              </td>
              <td v-else class="equity_align_right_heavy" :class="smallTextClass">
                <span class="progress-meter-value">
                  <meter value="0" min="0" max="100"></meter>
                  <span class="progress-percent">0%</span>
                </span>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td>{{ t("balanceLabel") }}</td>
              <td class="equity_align_right_heavy cash-balance-value" :class="{ redColor: isNegativeAmount(baseCurrencyAmount) || isNegativeAmount(money.a) }">
                <span class="base-currency-balance" v-html="isMoneyCurrencyBaseCurrency
                  ? '&nbsp;'
                  : formatPrice(baseCurrencyAmount, currency, 2).replace(' ', '&nbsp;')">
                </span>
                <span v-html="formatPrice(money.a, money.c, 2).replace(' ', '&nbsp;')"></span>
              </td>
            </tr>
            <tr>
              <td colspan="2">
                <span class="progress-meter-value cash-progress-meter">
                  <meter :value="moneyProgressPercent" min="0" max="100"></meter>
                  <span class="progress-percent">{{ parseNumber(moneyProgressPercent) }}%</span>
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <span class="label-with-sign-icon">
                  {{ t("inSettlementLabel") }}
                  <img :src="minusIcon" class="sign-icon" alt="-" />
                </span>
              </td>
              <td class="equity_align_right_heavy" :class="{ redColor: negativeSettlementAmount !== 0 }">
                {{ formatPrice(negativeSettlementAmount, money.c, 2).replace(" ", "&nbsp;") }}
              </td>
            </tr>
            <tr>
              <td>
                <span class="label-with-sign-icon">
                  {{ t("inSettlementLabel") }}
                  <img :src="plusIcon" class="sign-icon" alt="+" />
                </span>
              </td>
              <td class="equity_align_right_heavy">
                {{ formatPrice(positiveSettlementAmount, money.c, 2).replace(" ", "&nbsp;") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import minusIcon from "@/assets/images/Circle-plus-1.svg";
import plusIcon from "@/assets/images/Circle-plus.svg";

export default {
  name: "WalletMoneySummaryCard",
  data() {
    return {
      minusIcon,
      plusIcon
    };
  },
  props: {
    money: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      required: true
    },
    windowsWidth: {
      type: Number,
      required: true
    },
    translator: {
      type: Function,
      required: true
    },
    priceFormatter: {
      type: Function,
      required: true
    },
    numberParser: {
      type: Function,
      required: true
    },
    moneyRatioResolver: {
      type: Function,
      required: true
    },
    totalMoneyPercentResolver: {
      type: Function,
      required: true
    },
    totalBalancePercentResolver: {
      type: Function,
      required: true
    },
    settlementResolver: {
      type: Function,
      required: true
    },
    variant: {
      type: String,
      default: "summary"
    }
  },
  computed: {
    balancePercent() {
      return this.totalBalancePercentResolver(this.money);
    },
    balanceProgressPercent() {
      return this.clampPercent(this.balancePercent);
    },
    eurToBgnRatio() {
      return this.moneyRatioResolver("eur");
    },
    moneyPercent() {
      return this.totalMoneyPercentResolver(this.money);
    },
    moneyProgressPercent() {
      return this.clampPercent(this.moneyPercent);
    },
    moneyRatio() {
      return this.moneyRatioResolver(this.money.c);
    },
    isSummaryVariant() {
      return "summary" === this.variant;
    },
    isMoneyCurrencyBaseCurrency() {
      return this.money?.c?.toString().toUpperCase() === this.currency?.toString().toUpperCase();
    },
    baseCurrencyAmount() {
      if ("BGN" === this.currency) {
        return this.money.a * this.moneyRatio;
      }

      if ("EUR" === this.currency) {
        return this.money.a * this.moneyRatio / this.eurToBgnRatio;
      }

      return this.money.a;
    },
    settlementAmounts() {
      return this.settlementResolver(this.money.c) || { buy: 0, sell: 0 };
    },
    negativeSettlementAmount() {
      return -Math.abs(this.toNumber(this.settlementAmounts.buy));
    },
    positiveSettlementAmount() {
      return this.toNumber(this.settlementAmounts.sell);
    },
    smallTextClass() {
      return { "xsmall-font-size": 1200 > this.windowsWidth };
    }
  },
  methods: {
    t(key, ...args) {
      return this.translator(key, ...args);
    },
    formatPrice(...args) {
      return this.priceFormatter(...args);
    },
    parseNumber(...args) {
      return this.numberParser(...args);
    },
    toNumber(value) {
      const numberValue = parseFloat(value);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },
    isNegativeAmount(value) {
      return this.toNumber(value) < 0;
    },
    clampPercent(value) {
      const percent = parseFloat(value);
      if (!Number.isFinite(percent)) {
        return 0;
      }
      return Math.min(Math.max(percent, 0), 100);
    }
  }
};
</script>

<style scoped>
.xsmall-font-size {
  font-size: x-small;
}

.equity_align_right_heavy {
  font-weight: bold;
  text-align: right;
}

.redColor {
  color: #ff0000;
}

.cash-money-table td {
  font-size: 1rem;
  line-height: 1.35;
  padding: 0.45rem 0.5rem;
}

.cash-money-table {
  margin-bottom: 0;
}

.cash-card-title {
  align-items: center;
  display: flex;
  font-weight: bold;
  justify-content: center;
  margin: 0 auto;
  min-height: 2.25rem;
  text-align: center;
  width: 100%;
}

.cash-card-body {
  padding: 0.75rem 1rem;
}

.cash-balance-value {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.base-currency-balance {
  font-size: 1em;
  font-weight: normal;
  min-height: 1.2em;
}

.label-with-sign-icon {
  align-items: center;
  display: inline-flex;
  gap: 0.15rem;
  line-height: inherit;
}

.sign-icon {
  display: block;
  flex: 0 0 14px;
  height: 14px;
  transform: translateY(-2px);
  width: 14px;
}

.progress-meter-value {
  align-items: center;
  display: inline-flex;
  gap: 0.25rem;
  white-space: nowrap;
}

.cash-progress-meter {
  display: flex;
  width: 100%;
}

.cash-progress-meter meter {
  flex: 1 1 auto;
}

.progress-percent {
  display: inline-block;
  font-weight: bold;
  min-width: 3.75rem;
  text-align: right;
}
</style>
